// src/pages/HomeScreen.js
import React, { useEffect, useMemo, useState } from "react";
import {
SafeAreaView,
View,
Text,
StyleSheet,
Image,
TouchableOpacity,
Pressable,
ActivityIndicator,
Platform,
} from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import SecretCodeModal from "../components/SecretCodeModal";
import { useTranslation } from "../i18n";

const taxiImg = require("../../assets/taxi.png");
const cityLogo = require("../../assets/urban.png");
const tickCircle = require("../../assets/tickcircle.png"); // ✅ green verified
const xTickCircle = require("../../assets/xtickcircle.png"); // ✅ red not verified
const shieldLock = require("../../assets/shieldd.png");

const normalizeRef = (v) =>
String(v || "")
.trim()
.replace(/^\/+|\/+$/g, "")
.replace(/\s+/g, "")
.toUpperCase();

const getRefFromPathname = () => {
if (Platform.OS !== "web") return null;
try {
const path = window.location?.pathname || "";
const clean = path.replace(/^\/+|\/+$/g, "");
return clean || null;
} catch {
return null;
}
};

const GREEN = "#2F7D3A";
const RED = "#DC2626";
const MUTED = "rgba(107,114,128,0.95)";

export default function HomeScreen() {
const navigation = useNavigation();
const route = useRoute();

// ✅ matches your i18n/index.js
const { lang, setLang, t } = useTranslation();

const refFromParams = route?.params?.ref ? String(route.params.ref) : null;
// Read once: the address may be rewritten to the corrected plate below, and
// that must not trigger a second lookup (or a second counted scan).
const [refFromPath] = useState(getRefFromPathname);

const plateRef = useMemo(() => {
const raw = refFromParams || refFromPath || null;
const n = normalizeRef(raw);
return n.length ? n : null;
}, [refFromParams, refFromPath]);

const [langOpen, setLangOpen] = useState(false);
const [codeModalOpen, setCodeModalOpen] = useState(false);

const [loading, setLoading] = useState(false);
const [vehicle, setVehicle] = useState(null);
const [hasError, setHasError] = useState(false);

// ✅ Stable UI status (prevents "comes and goes")
// modes: "UNKNOWN" | "CHECKING" | "VERIFIED" | "NOT_VERIFIED"
const [statusMode, setStatusMode] = useState("UNKNOWN");

// useEffect(() => {
// if (!plateRef) {
// setVehicle(null);
// setLoading(false);
// setHasError(false);
// setStatusMode("UNKNOWN");
// return;
// }

// setLoading(true);
// setHasError(false);
// setStatusMode("CHECKING");

// fetch(`https://api.taxi-yaounde.com/api/vehicles/${plateRef}/qr`)
// .then((res) => res.json())
// .then((json) => {
// if (json?.success) {
// setVehicle(json);
// setHasError(false);

// // ✅ NEW RULE: verified / not verified ONLY from boolean
// // json.verified === true -> VERIFIED
// // json.verified === false -> NOT_VERIFIED
// // missing/undefined -> NOT_VERIFIED (safe default) OR keep CHECKING
// if (json.verified === true) setStatusMode("VERIFIED");
// else if (json.verified === false) setStatusMode("NOT_VERIFIED");
// else setStatusMode("NOT_VERIFIED");
// } else {
// setVehicle(null);
// setHasError(true);
// setStatusMode("NOT_VERIFIED");
// }
// setLoading(false);
// })
// .catch(() => {
// setVehicle(null);
// setHasError(true);
// setStatusMode("NOT_VERIFIED");
// setLoading(false);
// });
// }, [plateRef]);

// In HomeScreen.js, modify the useEffect that fetches vehicle data
useEffect(() => {
  if (!plateRef) {
    setVehicle(null);
    setLoading(false);
    setHasError(false);
    setStatusMode("UNKNOWN");
    return;
  }

  setLoading(true);
  setHasError(false);
  setStatusMode("CHECKING");

  // Track the scan
  fetch(`https://api.taxi-yaounde.com/api/vehicles/scan/${encodeURIComponent(plateRef)}`)
    .catch(() => {
      // Silently fail - analytics should not break the user experience
    });

  // Fetch vehicle data
  fetch(`https://api.taxi-yaounde.com/api/vehicles/${encodeURIComponent(plateRef)}/qr`)
    .then((res) => res.json())
    .then((json) => {
      if (json?.success) {
        // Old sticker (plate corrected, or plate added to a vehicle first
        // registered by chassis): show the current plate in the address bar.
        const canonical = normalizeRef(json.canonicalRef);
        if (Platform.OS === "web" && canonical && canonical !== plateRef) {
          try {
            window.history.replaceState(window.history.state, "", `/${canonical}`);
          } catch {
            // Address bar update is cosmetic; the page already shows the right vehicle.
          }
        }

        setVehicle(json);
        setHasError(false);
        if (json.verified === true) setStatusMode("VERIFIED");
        else if (json.verified === false) setStatusMode("NOT_VERIFIED");
        else setStatusMode("NOT_VERIFIED");
      } else {
        setVehicle(null);
        setHasError(true);
        setStatusMode("NOT_VERIFIED");
      }
      setLoading(false);
    })
    .catch(() => {
      setVehicle(null);
      setHasError(true);
      setStatusMode("NOT_VERIFIED");
      setLoading(false);
    });
}, [plateRef]);

const plateToShow = useMemo(() => {
return vehicle?.identificationVehicule?.immatriculation || plateRef || "—";
}, [vehicle, plateRef]);

const FLAGS = { fr: "🇫🇷", en: "🇬🇧" };

// ✅ status UI derived from statusMode + translations
const statusUI = useMemo(() => {
if (statusMode === "VERIFIED") {
return { icon: tickCircle, color: GREEN, text: t("home.verified") };
}
if (statusMode === "NOT_VERIFIED") {
return { icon: xTickCircle, color: RED, text: t("home.notVerified") };
}
if (statusMode === "CHECKING") {
// ✅ keep space stable, but don't lie with "Not Verified"
return { icon: tickCircle, color: MUTED, text: "—" };
}
// UNKNOWN (no plate in URL)
return { icon: tickCircle, color: MUTED, text: "—" };
}, [statusMode, t]);

const handleConfirmCode = () => {
setCodeModalOpen(false);
navigation.navigate("official_control_screen", {
ref: vehicle?.canonicalRef || plateRef || plateToShow,
});
};

return (
<SafeAreaView style={styles.safe}>
<View style={styles.screen}>
{/* ===== Language Dropdown ===== */}
<View style={styles.langRow}>
<View style={styles.langDropdownWrap}>
<Pressable onPress={() => setLangOpen((v) => !v)} style={styles.langDropdownBtn}>
<Text style={styles.langDropdownText}>
{FLAGS[lang]} {lang === "fr" ? "FR" : "EN"}
</Text>
<Text style={styles.langDropdownCaret}>{langOpen ? "▲" : "▼"}</Text>
</Pressable>

{langOpen && (
<View style={styles.langDropdownMenu}>
<Pressable
onPress={() => {
setLang("fr");
setLangOpen(false);
}}
style={styles.langDropdownItem}
>
<Text style={styles.langDropdownItemText}>
{FLAGS.fr} {t("lang.fr")}
</Text>
</Pressable>

<Pressable
onPress={() => {
setLang("en");
setLangOpen(false);
}}
style={styles.langDropdownItem}
>
<Text style={styles.langDropdownItemText}>
{FLAGS.en} {t("lang.en")}
</Text>
</Pressable>
</View>
)}
</View>
</View>

{/* ===== HERO ===== */}
<View style={styles.heroWrap}>
<Image source={taxiImg} style={styles.taxi} resizeMode="contain" />
</View>

{/* ===== Status (ALWAYS visible, never "goes away") ===== */}
<View style={styles.statusRow}>
<Image source={statusUI.icon} style={styles.smallTick} resizeMode="contain" />
<Text style={[styles.statusText, { color: statusUI.color }]} numberOfLines={1}>
{statusUI.text}
</Text>
</View>

{/* ===== Plate (always visible) ===== */}
<View style={styles.plateWrap}>
<View style={styles.plateBox}>
<Text style={styles.plateText}>{plateToShow}</Text>
</View>
</View>

{/* ===== Loading indicator (optional) ===== */}
{loading && (
<View style={{ marginTop: 8 }}>
<ActivityIndicator size="small" color={MUTED} />
</View>
)}

{/* ===== Button (always visible) ===== */}
<TouchableOpacity
activeOpacity={0.92}
style={styles.officialBtn}
onPress={() => {
setLangOpen(false);
setCodeModalOpen(true);
}}
>
<Image source={shieldLock} style={styles.btnIcon} resizeMode="contain" />
<Text style={styles.btnText}>{t("home.officialControl")}</Text>
</TouchableOpacity>

{/* ===== Bottom logo ===== */}
<View style={styles.logoWrap}>
<Image source={cityLogo} style={styles.logo} resizeMode="contain" />
</View>

{/* ===== Secret Code Modal ===== */}
<SecretCodeModal
visible={codeModalOpen}
onClose={() => setCodeModalOpen(false)}
onConfirm={handleConfirmCode}
expectedCode={"9520"}
/>
</View>
</SafeAreaView>
);
}

const PLATE_WIDTH = 220;
const PLATE_HEIGHT = 40;
const DARK_GREEN = "#0F3C2F";

const styles = StyleSheet.create({
safe: { flex: 1, backgroundColor: "#F5F5F5" },

screen: {
flex: 1,
paddingTop: 14,
paddingHorizontal: 18,
alignItems: "center",
justifyContent: "flex-start",
},

langRow: {
width: "100%",
flexDirection: "row",
justifyContent: "flex-end",
marginBottom: 6,
zIndex: 999,
},

langDropdownWrap: { position: "relative" },

langDropdownBtn: {
height: 34,
minWidth: 92,
paddingHorizontal: 12,
borderRadius: 18,
backgroundColor: DARK_GREEN,
flexDirection: "row",
alignItems: "center",
justifyContent: "space-between",
gap: 10,
},

langDropdownText: {
color: "#FFFFFF",
fontSize: 14,
fontWeight: "800",
letterSpacing: 0.5,
lineHeight: 18,
},

langDropdownCaret: {
color: "rgba(255,255,255,0.9)",
fontSize: 12,
fontWeight: "900",
},

langDropdownMenu: {
position: "absolute",
top: 40,
right: 0,
width: 170,
borderRadius: 14,
backgroundColor: "#FFFFFF",
overflow: "hidden",
shadowColor: "#000",
shadowOpacity: 0.18,
shadowRadius: 14,
shadowOffset: { width: 0, height: 10 },
elevation: 8,
borderWidth: 1,
borderColor: "rgba(0,0,0,0.06)",
zIndex: 9999,
},

langDropdownItem: { paddingVertical: 12, paddingHorizontal: 14 },

langDropdownItemText: {
fontSize: 14,
fontWeight: "700",
color: "#111827",
lineHeight: 18,
},

heroWrap: { width: "100%", alignItems: "center", justifyContent: "center", marginTop: 1 },
taxi: { width: "100%", height: 200 },

// ✅ fixed height prevents jump/flicker
statusRow: {
flexDirection: "row",
alignItems: "center",
marginTop: 1,
gap: 12,
minHeight: 64,
},
smallTick: { width: 56, height: 56 },
statusText: { fontSize: 30, fontWeight: "600" },

plateWrap: { marginTop: 10, alignItems: "center" },
plateBox: {
width: PLATE_WIDTH,
height: PLATE_HEIGHT,
borderRadius: 16,
borderWidth: 3,
borderColor: GREEN,
backgroundColor: "#FFFFFF",
alignItems: "center",
justifyContent: "center",
},
plateText: {
fontSize: 30,
fontWeight: "600",
letterSpacing: 1,
color: "#111827",
textTransform: "uppercase",
},

errorText: {
marginTop: 6,
fontSize: 12,
color: "rgba(220,38,38,0.92)",
fontWeight: "700",
textAlign: "center",
},

officialBtn: {
marginTop: 12,
width: "72%",
height: 40,
backgroundColor: DARK_GREEN,
borderRadius: 16,
flexDirection: "row",
alignItems: "center",
justifyContent: "center",
paddingLeft: 54,
paddingRight: 16,
shadowColor: "#000",
shadowOpacity: 0.2,
shadowRadius: 16,
shadowOffset: { width: 0, height: 12 },
elevation: 7,
},
btnIcon: { position: "absolute", left: 16, width: 30, height: 30 },
btnText: { color: "#FFFFFF", fontSize: 20, fontWeight: "700" },

logoWrap: { marginTop: 10, alignItems: "center", width: "100%", flex: 1 },
logo: { width: 220, height: 210 },
});